import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  List,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SportsTennisOutlinedIcon from '@mui/icons-material/SportsTennisOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import MilitaryTechOutlinedIcon from '@mui/icons-material/MilitaryTechOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import CalendarViewDayOutlinedIcon from '@mui/icons-material/CalendarViewDayOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import SubdirectoryArrowRightRoundedIcon from '@mui/icons-material/SubdirectoryArrowRightRounded';
import { toast } from 'react-toastify';
import {
  BAR_HOME_PATH,
  OFFICIAL_TOURNAMENTS_PATH,
  activeTournamentAccess,
  fetchMyClubs,
  managedTournamentsOf,
  takeClubNotice,
  useAuth,
} from '../../contexts/AuthContext';
import { appleTokens, tint } from '../../theme/appleTokens';
import { ProfileMenu } from './TopbarControls';
import { leagueSpaceUrl } from '../../services/platformSpace';
import { v2Api } from '../../services/api/v2Client';
import { adoptLeagueSession, hasLeagueSession } from '../../league/sessionHandover';
import ManagementModeDialog from './ManagementModeDialog';
import LockScreen from './LockScreen';
import { verifyErpPin } from '../../services/api';
import { BreadcrumbDetailContext } from './ui/breadcrumb';
import { clubLogoUrl, useClubPublicConfig } from '../../services/clubPublicConfig';
import { loadSports } from '../../services/sportsCatalog';
import { isRouteEnabled } from '../../services/clubModules';
import { getClubStorageItem, getCurrentClubSlug, setClubStorageItem } from '../../services/currentClub';

const t = appleTokens;
const s = t.sidebar;

const DRAWER_W_EXPANDED = 248;
const DRAWER_W_COLLAPSED = 68;
const TOPBAR_H = 56;
const DRAWER_COLLAPSED_STORAGE_KEY = 'squash_erp_drawer_collapsed';
const ADVANCED_UNLOCK_STORAGE_KEY = 'squash_erp_advanced_unlocked';
/** Écran verrouillé (bouton cadenas) : mémorisé pour rester verrouillé après un rechargement. */
const SCREEN_LOCK_STORAGE_KEY = 'squash_erp_screen_locked';

function readScreenLocked() {
  try {
    return getClubStorageItem(SCREEN_LOCK_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeScreenLocked(locked) {
  try {
    setClubStorageItem(SCREEN_LOCK_STORAGE_KEY, locked ? '1' : '0');
  } catch {
    // Stockage indisponible : le verrou vaut pour cette page seulement.
  }
}

// Pages hors menu : libellé affiché dans le fil d'Ariane de la topbar.
const EXTRA_PAGES = [
  { to: '/app/season-mirror', section: 'Box league', label: 'Miroir de saison' },
  { to: '/app/motw-predictions', section: 'Communication', label: 'Match de la semaine' },
  { to: '/app/classement-print', section: 'Box league', label: 'Impression du classement' },
  { to: '/app/db-tools', section: 'Administration', label: 'Outils base de données' },
  { to: '/app/terrains/eclairage', section: 'Agenda', label: 'Éclairage' },
];

function readAdvancedUnlocked() {
  try {
    return getClubStorageItem(ADVANCED_UNLOCK_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export default function ErpShell() {
  const clubConfig = useClubPublicConfig();
  // Catalogue des sports (terrains, tarifs, règles, planning) — GET /Sport exige un JWT, donc chargé ici.
  useEffect(() => {
    void loadSports();
  }, []);
  const clubName = clubConfig['club.name'];
  // Barre latérale sombre : logo blanc du club (repli : logo embarqué).
  const logoSrc = clubLogoUrl(clubConfig, 'white');
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(() => {
    try {
      return localStorage.getItem(DRAWER_COLLAPSED_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const { user, logout, switchClub } = useAuth();
  // Un compte = un club (staff compris) : GET /Player/my_clubs ne renvoie plusieurs clubs (tous les clubs actifs)
  // qu'à un super-admin de la Ligue, seul à voir le sélecteur « Clubs de la Ligue » du menu profil. Le rôle est relu
  // (GET /League/me) : avec un seul club actif (les autres suspendus), l'admin de la Ligue garde l'accès à la Ligue.
  const [leagueClubs, setLeagueClubs] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const leagueAdmin = v2Api
      .get('/League/me')
      .then(({ data }) => Boolean(data?.is_league_admin))
      .catch(() => false);
    Promise.all([fetchMyClubs(), leagueAdmin])
      .then(([clubs, isLeagueAdmin]) => {
        if (!cancelled) setLeagueClubs(clubs.length > 1 || (isLeagueAdmin && clubs.length > 0) ? clubs : []);
      })
      .catch(() => {
        /* sélecteur masqué : le compte garde son club */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const currentClubSlug = getCurrentClubSlug();
  // Connexion sur un autre club que celui demandé : message reçu avant le rechargement, affiché une fois.
  useEffect(() => {
    const notice = takeClubNotice();
    if (notice) toast.info(notice, { autoClose: 8000 });
  }, []);
  const onSwitchClub = async (club) => {
    const result = await switchClub(club);
    if (result.ok) return;
    if (result.error === 'single_club') {
      // Compte de club (plus super-admin) : sélecteur retiré.
      setLeagueClubs([]);
      toast.error('Ce compte n’a accès qu’à son club : le changement de club est réservé aux administrateurs de la Ligue.');
    } else {
      toast.error(`Impossible de passer au club « ${club.name} ».`);
    }
  };
  /**
   * Espace Ligue sans reconnexion : sur la même origine (pas encore de domaine racine), si la Ligue n'a pas déjà sa
   * session, une nouvelle session indépendante est ouverte pour elle (`switch_club` vers ce même club, admin de la
   * Ligue) — jamais la session de l'ERP partagée. Sur l'hôte `lfs.<racine>`, le stockage diffère : connexion MySquash.
   */
  const openLeagueSpace = async (club) => {
    const target = leagueSpaceUrl();
    let sameOrigin = false;
    try {
      sameOrigin = new URL(target).origin === window.location.origin;
    } catch {
      sameOrigin = false;
    }
    if (sameOrigin && club?.club_id && !hasLeagueSession()) {
      try {
        const { data } = await v2Api.post('/Player/auth/switch_club', { club_id: club.club_id, device_label: 'mysquash-web' });
        adoptLeagueSession(data);
      } catch {
        // Pas de session : l'espace Ligue affichera sa page de connexion.
      }
    }
    window.location.assign(target);
  };
  const who = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;
  // Staff de tournoi sans rôle d'admin dans le club : staff d'un tournoi officiel de la Ligue (docs/OFFICIAL_TOURNAMENTS.md
  // § 4) et / ou gestionnaire de tournois du club (docs/TOURNAMENT_MANAGERS.md) : vue limitée.
  const isTournamentStaff = user?.role === 'tournament_staff';
  const officialAccess = useMemo(() => activeTournamentAccess(user?.tournamentAccess), [user?.tournamentAccess]);
  const hasOfficialAccess = officialAccess.length > 0;
  const managedTournaments = useMemo(() => managedTournamentsOf(user), [user]);
  const [advancedUnlocked, setAdvancedUnlocked] = useState(() => readAdvancedUnlocked());
  const [advancedDialogOpen, setAdvancedDialogOpen] = useState(false);
  const [screenLocked, setScreenLocked] = useState(() => readScreenLocked());
  // Nom de l'élément affiché par une page de détail (joueur, tournoi…), publié via useBreadcrumbDetail.
  const [crumbDetail, setCrumbDetail] = useState(null);

  const mdDrawerWidth = drawerCollapsed ? DRAWER_W_COLLAPSED : DRAWER_W_EXPANDED;

  // Menu : le club (Agenda = réservations des terrains, membres, ventes, communication, badges), les compétitions
  // (matchs, box — son tableau de bord est le premier onglet —, tournois, interclubs) et, à part sous un filet, la
  // configuration. Les paramètres de saison (points, règles) s'ouvrent depuis le tableau de bord de Box. `advanced` :
  // visible une fois le mode avancé déverrouillé (hors mode avancé, le bar garde Agenda, Matchs, Box — tableau de bord et
  // vue joueur de la file d'attente seulement — et Boutique).
  const navSections = useMemo(
    () => [
      {
        // Le club : l'agenda des terrains, les membres, les ventes et ce qu'il communique aux joueurs (à la une,
        // notifications, badges).
        label: 'Club',
        items: [
          { to: '/app/terrains', label: 'Agenda', Icon: CalendarViewDayOutlinedIcon },
          { to: '/app/players', label: 'Joueurs', Icon: PeopleOutlineIcon, advanced: true },
          { to: '/app/shop', label: 'Boutique', Icon: StorefrontOutlinedIcon },
          { to: '/app/home-highlights', label: 'À la une', Icon: NewspaperOutlinedIcon, advanced: true },
          { to: '/app/notifications', label: 'Notifications', Icon: NotificationsNoneOutlinedIcon, advanced: true },
          { to: '/app/badges', label: 'Badges', Icon: MilitaryTechOutlinedIcon, advanced: true },
        ],
      },
      {
        // Compétitions : les matchs, la box league (onglets Tableau de bord, Configuration, File d'attente), les tournois,
        // les interclubs.
        label: 'Compétitions',
        items: [
          { to: '/app/matches', label: 'Matchs', Icon: SportsTennisOutlinedIcon },
          { to: '/app/catalog', label: 'Box', Icon: AccountTreeOutlinedIcon },
          { to: '/app/tournaments', label: 'Tournois', Icon: EmojiEventsOutlinedIcon, advanced: true },
          // Admin du club nommé responsable / adjoint d'un tournoi officiel (ici ou ailleurs) : ses tournois de la Ligue.
          ...(hasOfficialAccess
            ? [{ to: OFFICIAL_TOURNAMENTS_PATH, label: 'Tournois officiels', Icon: WorkspacePremiumOutlinedIcon }]
            : []),
          { to: '/app/interclubs', label: 'Interclubs', Icon: ShieldOutlinedIcon, advanced: true },
        ],
      },
      {
        // Réglages du club, seuls en bas (sans titre de section).
        label: null,
        items: [{ to: '/app/configuration', label: 'Configuration', Icon: TuneOutlinedIcon, advanced: true }],
      },
    ]
      // Modules désactivés (Configuration → Modules) : entrées masquées, sections vides retirées.
      // Configuration n'est rattachée à aucun module : toujours visible.
      .map((sec) => ({ ...sec, items: sec.items.filter((it) => isRouteEnabled(clubConfig, it.to)) }))
      .filter((sec) => sec.items.length > 0),
    [clubConfig, hasOfficialAccess]
  );

  // Gestionnaire de tournois du club : Compétitions → Matchs, puis la fiche de chacun de ses tournois de l'app (3 au plus).
  const managerItems = useMemo(() => {
    if (!managedTournaments.length) return [];
    const appTournaments = managedTournaments.filter((m) => m.tournament_id);
    const shortcuts = appTournaments.length <= 3 ? appTournaments : [];
    return [
      { to: '/app/matches', label: 'Matchs', Icon: SportsTennisOutlinedIcon },
      ...shortcuts.map((m) => ({
        to: `/app/tournaments/${m.tournament_id}`,
        label: m.name,
        Icon: SubdirectoryArrowRightRoundedIcon,
        indent: true,
      })),
    ];
  }, [managedTournaments]);

  // Staff de tournoi : « Mes tournois officiels », plus un raccourci par tournoi tant qu'il y en a 3 au plus.
  const tournamentStaffItems = useMemo(() => {
    const shortcuts = officialAccess.length <= 3 ? officialAccess : [];
    return [
      { to: OFFICIAL_TOURNAMENTS_PATH, label: 'Mes tournois officiels', Icon: WorkspacePremiumOutlinedIcon, end: shortcuts.length > 0 },
      ...shortcuts.map((a) => ({
        to: `${OFFICIAL_TOURNAMENTS_PATH}/${a.tournament_id}`,
        label: a.name,
        Icon: SubdirectoryArrowRightRoundedIcon,
        indent: true,
      })),
    ];
  }, [officialAccess]);

  // Fil d'Ariane de la topbar : section › page, déduit de l'URL courante.
  const crumb = useMemo(() => {
    const path = location.pathname;
    const candidates = [
      ...navSections.flatMap((sec) => sec.items.map((it) => ({ to: it.to, section: sec.label, label: it.label }))),
      ...EXTRA_PAGES,
      { to: OFFICIAL_TOURNAMENTS_PATH, section: null, label: isTournamentStaff ? 'Mes tournois officiels' : 'Tournois officiels' },
      // Gestionnaire : ses pages (le menu complet n'est pas le sien) ; « Tournois » ramène à l'écran Matchs.
      ...(isTournamentStaff
        ? [
            { to: '/app/matches', section: 'Compétitions', label: 'Matchs' },
            { to: '/app/tournaments', section: 'Compétitions', label: 'Tournois' },
          ]
        : []),
    ];
    const match = candidates
      .filter((c) => path === c.to || path.startsWith(`${c.to}/`))
      .sort((a, b) => b.to.length - a.to.length)[0];
    if (!match) return null;
    const isDetail = path !== match.to;
    return { section: match.section && match.section !== match.label ? match.section : null, label: match.label, to: match.to, detail: isDetail };
  }, [location.pathname, navSections, isTournamentStaff]);

  const toggleDrawerCollapsed = () => {
    setDrawerCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(DRAWER_COLLAPSED_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const lockAdvanced = () => {
    try {
      setClubStorageItem(ADVANCED_UNLOCK_STORAGE_KEY, '0');
    } catch {}
    setAdvancedUnlocked(false);
    setAdvancedDialogOpen(false);
    // Page avancée ouverte (tableau de bord, gestion des box…) : retour à l'accueil du bar.
    const path = location.pathname;
    const onAdvancedPage = navSections.some((sec) => sec.items.some((it) => it.advanced && (path === it.to || path.startsWith(`${it.to}/`))));
    if (onAdvancedPage) navigate(BAR_HOME_PATH, { replace: true });
  };

  const openAdvancedDialog = () => setAdvancedDialogOpen(true);

  /** Code de la gestion avancée (Configuration → Comptes & sécurité), vérifié par l'API. */
  const tryAdvancedUnlock = async (code) => {
    const ok = await verifyErpPin('advanced', code);
    if (ok) {
      try {
        setClubStorageItem(ADVANCED_UNLOCK_STORAGE_KEY, '1');
      } catch {}
      setAdvancedUnlocked(true);
      setAdvancedDialogOpen(false);
    }
    return ok;
  };

  const lockScreen = () => {
    writeScreenLocked(true);
    setScreenLocked(true);
  };
  const unlockScreen = () => {
    writeScreenLocked(false);
    setScreenLocked(false);
  };

  // `end` : actif seulement sur ce chemin exact (entrée parente de raccourcis) ; `indent` : raccourci sous une entrée.
  const renderNavItem = ({ to, label, Icon, end, indent }, compact) => (
    <Tooltip key={to} title={compact ? label : ''} placement="right" enterDelay={300}>
      <ButtonBase
        component={NavLink}
        to={to}
        end={end}
        onClick={() => isMdDown && setMobileOpen(false)}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: compact ? 'center' : 'flex-start',
          gap: 1.25,
          width: compact ? 44 : 'auto',
          height: 36,
          mx: compact ? 'auto' : 1.25,
          ml: compact ? 'auto' : indent ? 3 : 1.25,
          my: '2px',
          px: compact ? 0 : 1.25,
          borderRadius: t.radius.md,
          color: s.text,
          ...t.type.small,
          fontWeight: 500,
          transition: 'background-color 0.15s ease, color 0.15s ease',
          '& .nav-icon': { fontSize: 19, color: 'inherit', flexShrink: 0 },
          '&:hover': { bgcolor: s.hover, color: s.textActive },
          '&.active': {
            bgcolor: s.active,
            color: s.textActive,
            fontWeight: 600,
            '& .nav-icon': { color: t.accent },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: compact ? -12 : -10,
              top: 8,
              bottom: 8,
              width: 3,
              borderRadius: 3,
              bgcolor: t.accent,
            },
          },
        }}
      >
        <Icon className="nav-icon" />
        {!compact ? (
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
          </Box>
        ) : null}
      </ButtonBase>
    </Tooltip>
  );

  const renderSectionLabel = (label, compact) => {
    if (compact) return <Box key={`sep-${label}`} sx={{ height: '1px', bgcolor: s.border, mx: 2, my: 1.25 }} />;
    return (
      <Typography key={`lbl-${label}`} sx={{ ...t.type.overline, px: 2.5, pt: 2, pb: 0.75, color: s.section }}>
        {label}
      </Typography>
    );
  };

  const drawerContent = (compact) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', bgcolor: s.bg }}>
      <Box
        sx={{
          height: TOPBAR_H,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: compact ? 'center' : 'flex-start',
          gap: 1.25,
          px: compact ? 0 : 2.25,
          borderBottom: '1px solid',
          borderColor: s.border,
        }}
      >
        <Box component="img" src={logoSrc} alt={clubConfig['club.name']} sx={{ height: 30, width: compact ? 30 : 'auto', maxWidth: compact ? 30 : 96, borderRadius: t.radius.md, flexShrink: 0, objectFit: 'contain' }} />
        {!compact ? (
          // Nom du club seul, en grand, à côté du logo (plus de sous-titre).
          <Typography sx={{ ...t.type.cardTitle, fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.01em', color: s.textActive, lineHeight: 1.2, minWidth: 0 }} noWrap>
            {clubName}
          </Typography>
        ) : null}
      </Box>

      <List component="div" sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.12)' } }}>
        {isTournamentStaff ? (
          <>
            {managerItems.length ? (
              <>
                {renderSectionLabel('Compétitions', compact)}
                {managerItems.map((item) => renderNavItem(item, compact))}
              </>
            ) : null}
            {hasOfficialAccess || !managerItems.length ? (
              <>
                {renderSectionLabel('Tournois officiels', compact)}
                {tournamentStaffItems.map((item) => renderNavItem(item, compact))}
              </>
            ) : null}
          </>
        ) : (
          navSections.map((section, index) => {
            const items = section.items.filter((item) => advancedUnlocked || !item.advanced);
            if (!items.length) return null;
            return (
              <React.Fragment key={section.label || `section-${index}`}>
                {section.label ? (
                  renderSectionLabel(section.label, compact)
                ) : index > 0 ? (
                  // Section sans titre après les autres (Configuration) : un simple filet la sépare.
                  <Box sx={{ height: '1px', bgcolor: s.border, mx: 2, my: 1.25 }} />
                ) : null}
                {items.map((item) => renderNavItem(item, compact))}
              </React.Fragment>
            );
          })
        )}
      </List>


      {!isMdDown ? (
        <Box sx={{ borderTop: '1px solid', borderColor: s.border, p: 1, display: 'flex', justifyContent: compact ? 'center' : 'flex-end' }}>
          <Tooltip title={drawerCollapsed ? 'Développer le menu' : 'Réduire le menu'} placement="right">
            <IconButton
              size="small"
              onClick={toggleDrawerCollapsed}
              aria-label={drawerCollapsed ? 'Développer le menu latéral' : 'Réduire le menu latéral'}
              sx={{ color: s.section, '&:hover': { bgcolor: s.hover, color: s.textActive } }}
            >
              {drawerCollapsed ? <KeyboardDoubleArrowRightRoundedIcon fontSize="small" /> : <KeyboardDoubleArrowLeftRoundedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
    </Box>
  );

  const transition = (props) =>
    theme.transitions.create(props, { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen });

  const drawerPaperSx = {
    width: DRAWER_W_EXPANDED,
    boxSizing: 'border-box',
    bgcolor: s.bg,
    border: 0,
    transition: transition('width'),
  };

  return (
    <BreadcrumbDetailContext.Provider value={setCrumbDetail}>
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: t.canvas }}>
      <AppBar
        data-print-hide
        position="fixed"
        elevation={0}
        sx={{
          // Translucide sur la couleur de la page (claire ou sombre, theme/colorMode.js).
          bgcolor: tint(t.canvas, 0.86),
          backdropFilter: 'saturate(180%) blur(12px)',
          color: t.label,
          width: { md: `calc(100% - ${mdDrawerWidth}px)` },
          ml: { md: `${mdDrawerWidth}px` },
          borderBottom: '1px solid',
          borderColor: t.separator,
          transition: transition(['width', 'margin']),
        }}
      >
        <Toolbar disableGutters sx={{ minHeight: `${TOPBAR_H}px !important`, px: { xs: 1.5, md: 3 }, gap: 1.5 }}>
          <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ display: { md: 'none' }, color: t.label }} aria-label="Ouvrir le menu">
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {crumb ? (
              <>
                {crumb.section ? (
                  <>
                    <Typography sx={{ ...t.type.small, color: t.tertiaryLabel, display: { xs: 'none', sm: 'block' } }} noWrap>
                      {crumb.section}
                    </Typography>
                    <ChevronRightRoundedIcon sx={{ fontSize: 16, color: t.tertiaryLabel, display: { xs: 'none', sm: 'block' } }} />
                  </>
                ) : null}
                {crumb.detail ? (
                  <>
                    {/* Sur une page de détail, la page parente est un lien (ex. « Joueurs » → liste des joueurs). */}
                    <ButtonBase
                      component={RouterLink}
                      to={crumb.to}
                      sx={{
                        ...t.type.smallStrong,
                        color: t.secondaryLabel,
                        borderRadius: t.radius.sm,
                        px: 0.5,
                        mx: -0.5,
                        whiteSpace: 'nowrap',
                        '&:hover': { color: t.label, bgcolor: t.surfaceHover },
                      }}
                    >
                      {crumb.label}
                    </ButtonBase>
                    <ChevronRightRoundedIcon sx={{ fontSize: 16, color: t.tertiaryLabel }} />
                    {crumbDetail?.parent ? (
                      <>
                        <ButtonBase
                          component={RouterLink}
                          to={crumbDetail.parent.to}
                          sx={{
                            ...t.type.smallStrong,
                            color: t.secondaryLabel,
                            borderRadius: t.radius.sm,
                            px: 0.5,
                            mx: -0.5,
                            whiteSpace: 'nowrap',
                            '&:hover': { color: t.label, bgcolor: t.surfaceHover },
                          }}
                        >
                          {crumbDetail.parent.label}
                        </ButtonBase>
                        <ChevronRightRoundedIcon sx={{ fontSize: 16, color: t.tertiaryLabel }} />
                      </>
                    ) : null}
                    <Typography sx={{ ...t.type.smallStrong, color: t.label }} noWrap>
                      {crumbDetail?.label || 'Détail'}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ ...t.type.smallStrong, color: t.label }} noWrap>
                    {crumb.label}
                  </Typography>
                )}
              </>
            ) : (
              <Typography sx={{ ...t.type.smallStrong, color: t.label }}>{clubName}</Typography>
            )}
          </Box>
          {!isTournamentStaff ? (
            // Mode de l'ERP : Bar (matchs, terrains, commandes) ou Gestion (tout, code requis).
            <Box
              role="group"
              aria-label="Mode de l'ERP"
              sx={{ display: 'inline-flex', p: 0.25, gap: 0.25, borderRadius: t.radius.pill, bgcolor: t.surfaceSunken, border: '1px solid', borderColor: t.separator }}
            >
              {[
                { on: false, label: 'Bar', Icon: LocalBarOutlinedIcon, tip: 'Mode bar : matchs, terrains et commandes' },
                { on: true, label: 'Gestion', Icon: ShieldOutlinedIcon, tip: 'Mode gestion : toutes les pages (code requis)' },
              ].map(({ on, label, Icon, tip }) => {
                const selected = advancedUnlocked === on;
                return (
                  <Tooltip key={label} title={tip}>
                    <ButtonBase
                      aria-pressed={selected}
                      onClick={() => {
                        if (selected) return;
                        if (on) openAdvancedDialog();
                        else lockAdvanced();
                      }}
                      sx={{
                        gap: 0.5,
                        height: 28,
                        px: { xs: 1, sm: 1.25 },
                        borderRadius: t.radius.pill,
                        ...t.type.caption,
                        fontWeight: 600,
                        color: selected ? (on ? t.accentInk : t.label) : t.secondaryLabel,
                        bgcolor: selected ? (on ? t.accent : t.surface) : 'transparent',
                        boxShadow: selected ? t.shadow.card : 'none',
                        '&:hover': { color: t.label },
                      }}
                    >
                      <Icon sx={{ fontSize: 15 }} />
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {label}
                      </Box>
                    </ButtonBase>
                  </Tooltip>
                );
              })}
            </Box>
          ) : null}
          {/* Verrouillage par le code du club : pas pour un staff de tournoi externe au club. */}
          {!isTournamentStaff ? (
            <Tooltip title="Verrouiller l'écran">
              <IconButton onClick={lockScreen} aria-label="Verrouiller l'écran" sx={{ color: t.secondaryLabel, '&:hover': { color: t.label } }}>
                <LockOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          ) : null}
          <ProfileMenu
            user={user}
            who={who}
            onLogout={logout}
            hideAdvanced
            clubs={leagueClubs}
            currentClubSlug={currentClubSlug}
            onSwitchClub={onSwitchClub}
            onSelectLeague={() => void openLeagueSpace(leagueClubs.find((c) => c.slug === currentClubSlug))}
          />
        </Toolbar>
      </AppBar>

      <Box component="nav" data-print-hide sx={{ width: { md: mdDrawerWidth }, flexShrink: { md: 0 }, transition: transition('width') }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': drawerPaperSx }}
        >
          {drawerContent(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { ...drawerPaperSx, width: mdDrawerWidth, overflowX: 'hidden' },
          }}
        >
          {drawerContent(drawerCollapsed)}
        </Drawer>
      </Box>

      <Box
        component="main"
        data-print-main
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${mdDrawerWidth}px)` },
          mt: `${TOPBAR_H}px`,
          minWidth: 0,
          // Hauteur fixe (et non minimale) : une page « liste » (PageContainer fill) remplit exactement l'écran et son
          // tableau défile en interne ; une page plus haute défile dans cette zone. À l'impression, tout se déroule.
          height: `calc(100vh - ${TOPBAR_H}px)`,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflow: 'auto',
          '@media print': { height: 'auto', overflow: 'visible' },
          transition: transition('width'),
        }}
      >
        {/* Suspense local : pendant le chargement d'une page, le menu et la topbar restent affichés. */}
        <React.Suspense fallback={null}>
          <Outlet context={{ advancedUnlocked }} />
        </React.Suspense>
      </Box>

      <ManagementModeDialog open={advancedDialogOpen} onClose={() => setAdvancedDialogOpen(false)} onSubmit={tryAdvancedUnlock} />

      {screenLocked && !isTournamentStaff ? <LockScreen who={who} onVerify={(code) => verifyErpPin('lock', code)} onUnlocked={unlockScreen} /> : null}
    </Box>
    </BreadcrumbDetailContext.Provider>
  );
}
